import React, { useState } from 'react';
import { AppBar, Box, Toolbar, IconButton, Avatar, Menu, MenuItem, useTheme, useMediaQuery, Typography, Badge, Tooltip, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
interface TopBarProps {
  onMenuClick: () => void;
  onLogout: () => void;
}
const TopBar: React.FC<TopBarProps> = ({
  onMenuClick,
  onLogout
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };
  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };
  // Placeholder for last login time - in a real app, this would come from your authentication system
  const lastLoginTime = '21-06-2023 14:30:45';
  return <AppBar position="fixed" color="inherit" elevation={0} sx={{
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    backdropFilter: 'blur(8px)',
    zIndex: theme => theme.zIndex.drawer + 1
  }} data-id="element-2133">
      <Toolbar data-id="element-2134">
        {isMobile && <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onMenuClick} sx={{
        mr: 2,
        color: '#6B7280',
        '&:hover': {
          backgroundColor: '#F3F5F7'
        }
      }} data-id="element-2135">
            <Icon icon="lucide:menu" width={24} height={24} data-id="element-2136" />
          </IconButton>}
        {isLargeScreen && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 32,
        mr: 2,
        transition: 'all 200ms ease'
      }} data-id="element-2137" />}
        {isMobile && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 32,
        mr: 2
      }} data-id="element-2138" />}
        <Box sx={{
        flexGrow: 1
      }} data-id="element-2139" />
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2140">
          <Typography variant="body2" color="text.secondary" sx={{
          mr: 2,
          display: {
            xs: 'none',
            md: 'block'
          },
          fontSize: '0.75rem'
        }} data-id="element-2141">
            Last login: {lastLoginTime}
          </Typography>
          <Tooltip title="Dark mode" arrow data-id="element-2142">
            <IconButton size="small" sx={{
            color: '#64748B',
            backgroundColor: '#F1F5F9',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E2E8F0'
            },
            mr: 0.5
          }} data-id="element-2143">
              <Icon icon="lucide:moon" width={18} height={18} data-id="element-2144" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Help" arrow data-id="element-2145">
            <IconButton size="small" sx={{
            color: '#64748B',
            backgroundColor: '#F1F5F9',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E2E8F0'
            },
            mr: 0.5
          }} data-id="element-2146">
              <Icon icon="lucide:help-circle" width={18} height={18} data-id="element-2147" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications" arrow data-id="element-2148">
            <IconButton size="small" onClick={handleNotificationOpen} sx={{
            color: '#64748B',
            backgroundColor: '#F1F5F9',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#E2E8F0'
            },
            mr: 1
          }} data-id="element-2149">
              <Badge badgeContent={3} color="error" sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                height: 16,
                minWidth: 16
              }
            }} data-id="element-2150">
                <Icon icon="lucide:bell" width={18} height={18} data-id="element-2151" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Account settings" arrow data-id="element-2152">
            <IconButton edge="end" aria-label="account of current user" aria-haspopup="true" onClick={handleProfileMenuOpen} sx={{
            p: 0.5,
            border: '2px solid #E5E7EB',
            transition: 'all 200ms ease',
            '&:hover': {
              borderColor: '#0052CC'
            }
          }} data-id="element-2153">
              <Avatar sx={{
              width: 32,
              height: 32,
              backgroundColor: '#0052CC',
              fontWeight: 600
            }} data-id="element-2154">
                A
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      <Menu anchorEl={anchorEl} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} keepMounted transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{
      sx: {
        mt: 1.5,
        borderRadius: 2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        minWidth: 180
      }
    }} data-id="element-2155">
        <Box sx={{
        px: 2,
        py: 1.5
      }} data-id="element-2156">
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2157">
            Admin User
          </Typography>
          <Typography variant="caption" color="text.secondary" data-id="element-2158">
            admin@openremit.com
          </Typography>
        </Box>
        <Divider data-id="element-2159" />
        <MenuItem onClick={handleMenuClose} sx={{
        py: 1.5
      }} data-id="element-2160">
          <Icon icon="lucide:user" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-2161" />
          <Typography variant="body2" data-id="element-2162">Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{
        py: 1.5
      }} data-id="element-2163">
          <Icon icon="lucide:settings" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-2164" />
          <Typography variant="body2" data-id="element-2165">Settings</Typography>
        </MenuItem>
        <Divider data-id="element-2166" />
        <MenuItem onClick={handleLogout} sx={{
        py: 1.5,
        color: 'error.main'
      }} data-id="element-2167">
          <Icon icon="lucide:log-out" width={16} height={16} style={{
          marginRight: 8
        }} data-id="element-2168" />
          <Typography variant="body2" data-id="element-2169">Logout</Typography>
        </MenuItem>
      </Menu>
      <Menu anchorEl={notificationAnchor} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} keepMounted transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} open={Boolean(notificationAnchor)} onClose={handleNotificationClose} PaperProps={{
      sx: {
        mt: 1.5,
        borderRadius: 2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        width: 320
      }
    }} data-id="element-2170">
        <Box sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-2171">
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2172">
            Notifications
          </Typography>
          <Typography variant="caption" color="primary" sx={{
          cursor: 'pointer',
          fontWeight: 500
        }} data-id="element-2173">
            Mark all as read
          </Typography>
        </Box>
        <Divider data-id="element-2174" />
        <Box sx={{
        maxHeight: 320,
        overflow: 'auto'
      }} data-id="element-2175">
          {[1, 2, 3].map(item => <Box key={item} sx={{
          p: 2,
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: item === 1 ? '#F9FAFB' : 'transparent',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }} data-id="element-2176">
              <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 1
          }} data-id="element-2177">
                <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: item === 1 ? '#0052CC' : 'transparent',
              mr: 1,
              mt: 0.5
            }} data-id="element-2178" />
                <Typography variant="body2" fontWeight={item === 1 ? 600 : 400} data-id="element-2179">
                  {item === 1 ? 'New partner registration request' : item === 2 ? 'System maintenance scheduled' : 'Compliance alert: Verification needed'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" data-id="element-2180">
                {item === 1 ? '5 minutes ago' : item === 2 ? '2 hours ago' : '1 day ago'}
              </Typography>
            </Box>)}
        </Box>
        <Box sx={{
        p: 1.5,
        textAlign: 'center',
        borderTop: '1px solid #E5E7EB'
      }} data-id="element-2181">
          <Typography variant="body2" color="primary" sx={{
          cursor: 'pointer',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline'
          }
        }} data-id="element-2182">
            View all notifications
          </Typography>
        </Box>
      </Menu>
    </AppBar>;
};
export default TopBar;